import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { Select } from '@components/select';
import { TextField } from '@components/textfield';
import { Button } from '@components/ui/button';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle
} from '@components/ui/sheet';
import { FlagBadge } from './FlagBadge';
import { useActivityFlagStore } from '../utils/useActivityFlagStore';
import { WorkoutFlagStatus } from '../../../../core/api/FitnessActivityClient';
import type { ActivityItem } from './ActivityList';

interface ActivitySidePanelProps {
    open: boolean;
    onClose: () => void;
    activity: ActivityItem | null;
    userId: string;
    onFlagSaved: (userFlagCreated: boolean) => void;
}

const statusOptions = Object.values(WorkoutFlagStatus).map((status) => ({
    label: status,
    value: status
}));

const typeLabels: Record<string, string> = {
    dailySteps: 'Daily Steps',
    workout: 'Workout',
    facilityVisit: 'Facility Visit'
};

const SectionCaption = ({ children }: { children: React.ReactNode }) => (
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {children}
    </span>
);

export const ActivitySidePanel = observer(
    ({ open, onClose, activity, onFlagSaved }: ActivitySidePanelProps) => {
        const {
            model,
            submitting,
            error,
            resetForm,
            initForm,
            createFlag,
            updateFlag
        } = useActivityFlagStore();

        useEffect(() => {
            if (open && activity) {
                if (activity.flag) {
                    initForm(activity.flag.status, activity.flag.comment);
                } else {
                    resetForm();
                }
            }
        }, [open, activity, initForm, resetForm]);

        if (!activity) return null;

        const existingFlag = activity.flag;

        const handleSubmit = async () => {
            if (existingFlag) {
                const result = await updateFlag(existingFlag.id, activity.id);
                if (result) {
                    onFlagSaved(false);
                    onClose();
                }
            } else {
                const result = await createFlag(activity.id);
                if (result) {
                    onFlagSaved(result.userFlagCreated);
                    onClose();
                }
            }
        };

        return (
            <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
                <SheetContent className="w-[420px] overflow-y-auto sm:max-w-[420px]">
                    <SheetHeader className="border-b border-primary/15 bg-gradient-to-br from-primary/10 to-primary/[0.04]">
                        <SheetTitle>
                            {typeLabels[activity.type] ?? activity.type}
                        </SheetTitle>
                        <SheetDescription className="font-mono text-xs">
                            ID {activity.id}
                        </SheetDescription>
                        {activity.timeRange && (
                            <p className="text-sm text-muted-foreground">
                                {activity.timeRange}
                            </p>
                        )}
                    </SheetHeader>

                    <div className="flex flex-col gap-6 px-4 pb-6">
                        <div className="rounded-md border bg-muted/30 p-4">
                            <SectionCaption>Details</SectionCaption>
                            <dl className="mt-3 divide-y divide-border">
                                {Object.entries(activity.details).map(
                                    ([key, value]) => (
                                        <div
                                            key={key}
                                            className="flex items-center justify-between gap-4 py-2 text-[0.8rem]"
                                        >
                                            <dt className="text-muted-foreground">
                                                {key}
                                            </dt>
                                            <dd className="text-right font-medium break-all">
                                                {value}
                                            </dd>
                                        </div>
                                    )
                                )}
                            </dl>
                        </div>

                        <div className="rounded-md border bg-muted/20 p-4">
                            <div className="mb-4 flex items-center gap-2">
                                <SectionCaption>
                                    {existingFlag
                                        ? 'Edit Flag'
                                        : 'Flag This Activity'}
                                </SectionCaption>
                                {existingFlag && (
                                    <FlagBadge status={existingFlag.status} />
                                )}
                            </div>

                            {existingFlag && (
                                <p className="mb-4 text-xs text-muted-foreground">
                                    Created by {existingFlag.createdBy}
                                    {existingFlag.modifiedBy
                                        ? ` · Modified by ${existingFlag.modifiedBy}`
                                        : ''}
                                </p>
                            )}

                            <div className="flex flex-col gap-4">
                                <Select
                                    label="Status"
                                    value={model.fields.status.value}
                                    options={statusOptions}
                                    onChange={(value: WorkoutFlagStatus) =>
                                        model.fields.status.onChange(value)
                                    }
                                />

                                <TextField
                                    label="Comment"
                                    type="text"
                                    multiline
                                    value={model.fields.comment.value}
                                    onChange={(value) =>
                                        model.fields.comment.onChange(
                                            String(value)
                                        )
                                    }
                                    error={
                                        model.fields.comment.hasError
                                            ? 'Required'
                                            : undefined
                                    }
                                />

                                {error && (
                                    <p className="text-sm text-destructive">
                                        {error}
                                    </p>
                                )}

                                <Button
                                    className="mt-1 w-full"
                                    onClick={handleSubmit}
                                    loading={submitting}
                                >
                                    {existingFlag
                                        ? 'Update Flag'
                                        : 'Create Flag'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>
        );
    }
);
